import {I18n} from 'aws-amplify/utils';
import {
    Drawer,
    ScrollArea,
} from '@mantine/core';

import {RecordForm} from '../forms/RecordForm';
import {
    emptyRecordFormValues,
    RecordFormValues,
    recordToFormValues,
} from '../forms/recordFormValues';
import {useUpsertRecordMutation} from '../hooks/useRecordMutations';
import {useRecordWriteCapabilities} from '../hooks/useRecordWriteCapabilities';
import {
    DnsRecord,
    DnsRecordChangeInput,
} from '../types';

// FNP-1192 — the add/edit drawer.
//
// One drawer for both, because the form is the same and the difference is
// entirely in what it starts from and what the mutation does with the result.
// `record` present means edit; absent means create.
export const RecordFormDrawer = (
    {
        connectionId,
        zoneId,
        record,
        opened,
        onClose,
    }: {
        connectionId?: string,
        zoneId?: string,
        // The record being edited. Absent for a create.
        record?: DnsRecord,
        opened: boolean,
        onClose: () => void,
    },
) => {

    const {
        supportedRecordTypes,
        supportedTypesAreProviderReported,
    } = useRecordWriteCapabilities(connectionId);

    const upsertMutation = useUpsertRecordMutation(connectionId, zoneId);

    const isEdit = Boolean(record);

    const initialValues: RecordFormValues = record
        // ⚠️ Only ever reached for a record the row action allowed to be
        // edited, which excludes pointers — see isEditableAsRdata. Pre-filling
        // from a pointer would offer its RESOLVED TARGET as the stored value.
        ? recordToFormValues(record)
        : emptyRecordFormValues(supportedRecordTypes[0] ?? 'A');

    const handleSubmit = async (
        change: DnsRecordChangeInput,
        values: RecordFormValues,
    ) => {
        // ⚠️ AWAITED, AND THE REJECTION IS NOT CAUGHT. The mutation's own
        // onError has already classified and notified it; catching it again
        // would either swallow the signal that keeps the drawer open, or
        // re-report a failure the operator has already been told about.
        await upsertMutation.mutateAsync({
            record: change,
            // The pair this edit started from, so a changed name or type
            // becomes a DELETE of the old plus an UPSERT of the new rather
            // than silently doubling the record.
            replacing: isEdit
                ? {
                    name: values.originalName,
                    type: values.originalType,
                }
                : undefined,
        });
        onClose();
    };

    return (
        <Drawer
            position={'right'}
            offset={10}
            opened={opened}
            onClose={onClose}
            title={isEdit ? I18n.get('Edit record') : I18n.get('Add record')}
            padding={'xl'}
            size={'lg'}
            scrollAreaComponent={ScrollArea.Autosize}
            transitionProps={{
                transition: 'slide-left',
                duration: 750,
                timingFunction: 'ease',
            }}
        >
            {/* Remounted per open and per record, so the form never shows the
                previous record's values or a stale validation error. */}
            {opened && (
                <RecordForm
                    key={record ? `${record.name}#${record.type}` : 'new'}
                    initialValues={initialValues}
                    supportedRecordTypes={supportedRecordTypes}
                    supportedTypesAreProviderReported={supportedTypesAreProviderReported}
                    isEdit={isEdit}
                    isSubmitting={upsertMutation.isPending}
                    onSubmit={handleSubmit}
                    onCancel={onClose}
                />
            )}
        </Drawer>
    );
};
